// frontend/src/pages/HomePage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Star, Calendar, Film, Sparkles, Loader, Bookmark, Play, TrendingUp, MessageSquare } from 'lucide-react';
import FallingHearts from '../components/FallingHearts';
import MovieCard from '../components/MovieCard';
import SeasonCard from '../components/SeasonCard';
import { moviesAPI } from '../services/api';
import { useAppContext } from '../context/AppContext';
import { HOLIDAY_COLLECTIONS } from '../utils/constants';

interface Movie {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  backdropUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  trailerUrl?: string;
  favoritesCount?: number;
  reviewsCount?: number;
}

const quotes = [
  { text: "I'm just a girl, standing in front of a boy, asking him to love her.", film: "Notting Hill (1999)" },
  { text: "You had me at hello.", film: "Jerry Maguire (1996)" },
  { text: "Love actually is all around.", film: "Love Actually (2003)" },
  { text: "To me, you are perfect.", film: "Love Actually (2003)" },
  { text: "I would rather have one breath of her hair, one kiss of her mouth than eternity without it.", film: "City of Angels (1998)" },
];

const seasonCollections = [
  { season: 'winter' as const, title: 'Winter Warmers', description: 'Cozy up with these winter romance classics' },
  { season: 'spring' as const, title: 'Spring Blossoms', description: 'Fresh starts and new beginnings' },
  { season: 'summer' as const, title: "Summer Lovin'", description: 'Sunny days and warm nights' },
  { season: 'autumn' as const, title: 'Autumn Romance', description: 'Fall in love all over again' },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { toggleWatchlist, state } = useAppContext();

  const [romcomOfTheDay, setRomcomOfTheDay] = useState<Movie | null>(null);
  const [trendingMovies, setTrendingMovies] = useState<Movie[]>([]);
  const [isLoadingHero, setIsLoadingHero] = useState(true);
  const [isLoadingTrending, setIsLoadingTrending] = useState(true);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [activeQuote, setActiveQuote] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % quotes.length);
      setActiveQuote(prev => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsLoadingHero(true);
        const response = await moviesAPI.getRomcomOfTheDay();
        setRomcomOfTheDay(response.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingHero(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsLoadingTrending(true);
        const response = await moviesAPI.getTrending(6);
        setTrendingMovies(response.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingTrending(false);
      }
    };
    fetch();
  }, []);

  const isRomcomWatchlisted = romcomOfTheDay ? state.watchlist.includes(romcomOfTheDay.id) : false;
  const romcomRating = romcomOfTheDay?.voteAverage ? (romcomOfTheDay.voteAverage / 2).toFixed(1) : null;

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-[#faf8f6]">

      <FallingHearts />

      {/* ── HERO ── */}
      <section className="relative pt-16 pb-20 px-6 md:px-12">
        <div className="absolute inset-0 bg-gradient-to-br from-romcom-pink/10 via-transparent to-romcom-blue/5 -z-10" />

        <div className="absolute top-0 right-0 w-96 h-96 bg-romcom-pink/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-romcom-blue/10 rounded-full blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left: Title */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 text-center lg:text-left"
            >
              <div className="flex justify-center lg:justify-start mb-6">
                <div className="relative">
                  <img
                    src="/logo2.jpeg"
                    alt="RomComThis Logo"
                    className="w-24 h-24 object-contain animate-pulse-gentle"
                  />
                  <Heart
                    className="absolute -top-2 -right-2 w-5 h-5 text-romcom-purple fill-current animate-bounce"
                  />
                </div>
              </div>

              <h1 className="text-3xl md:text-3xl lg:text-6xl font-playfair font-bold mb-6 leading-tight">
                <span className="block text-gray-800"></span>
                <span className="block bg-gradient-to-r from-romcom-red via-romcom-pink to-romcom-purple bg-clip-text text-transparent">
                  RomComTHIS
                </span>
                <span className="block text-gray-600">For the Love of RomComs</span>
              </h1>

              <p className="text-base md:text-xl text-gray-500 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                A corner on the internet for romcoms. Discover, rate, and share your favorite
                feel-good movies sorted by seasons, moods, and holidays.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <button
                  onClick={() => navigate('/movies')}
                  className="btn-primary flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Exploring
                </button>
                <button
                  onClick={() => navigate('/quiz')}
                  className="btn-secondary flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg"
                >
                  <Heart className="w-5 h-5" />
                  💊 Get a Prescription 
                </button>
              </div>
            </motion.div>

            {/* Right: Stats + mood tags */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex-1 w-full max-w-md lg:max-w-none"
            >
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[
                  { icon: Film, label: 'Romcoms', value: '2,000+', color: 'from-romcom-pink/30 to-romcom-red/20', iconColor: 'text-romcom-red' },
                  { icon: TrendingUp, label: 'Trending Now', value: 'Daily Picks', color: 'from-romcom-blue/20 to-romcom-navy/10', iconColor: 'text-romcom-blue' },
                  { icon: Calendar, label: 'Seasonal Picks', value: '4 Seasons', color: 'from-romcom-gold/20 to-romcom-gold/10', iconColor: 'text-romcom-gold' },
                  { icon: MessageSquare, label: 'Community', value: 'Join Us', color: 'from-romcom-purple/20 to-romcom-mauve/10', iconColor: 'text-romcom-purple' },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className={`bg-gradient-to-br ${stat.color} rounded-2xl p-6 border border-white/60 shadow-sm`}
                  >
                    <stat.icon className={`w-8 h-8 ${stat.iconColor} mb-3`} />
                    <div className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Mood tags */}
              <div className="flex flex-wrap gap-2">
                {['Feel Good', 'Cozy', 'Tearjerker', 'Classic', 'Funny', 'Emotional', 'Comfort'].map((mood, i) => (
                  <motion.button
                    key={mood}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + i * 0.05 }}
                    onClick={() => navigate(`/movies?mood=${mood.toLowerCase().replace(' ', '_')}`)}
                    className="px-4 py-2 bg-gradient-to-r from-romcom-pink/20 to-romcom-purple/20 border border-romcom-pink/30 text-romcom-red rounded-full text-sm font-medium hover:from-romcom-pink/40 hover:to-romcom-purple/40 hover:shadow-md transition-all shadow-sm backdrop-blur-sm"
                  >
                    {mood}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── ROMCOM OF THE DAY ── */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Film className="w-7 h-7 text-romcom-red" />
            <h2
              className="text-6xl font-bold text-romcom-red"
              style={{
                fontFamily: "'Fredoka One', cursive",
              }}
            >
              Romcom of the Da
              <span className="relative inline-block">
                y
                <span className="absolute -top-6 -right-2 text-2xl" style={{ WebkitTextStroke: '0px' }}>
                  💗
                </span>
              </span>
            </h2>
            <span className="ml-auto flex items-center gap-1 bg-yellow-50 text-yellow-600 px-3 py-1 rounded-full text-sm font-medium border border-yellow-200">
              <Star className="w-4 h-4 fill-current" />
              Featured
            </span>
          </div>

          {isLoadingHero ? (
            <div className="flex justify-center py-16">
              <Loader className="w-10 h-10 text-romcom-red animate-spin" />
            </div>
          ) : romcomOfTheDay ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#faf8f6] rounded-3xl shadow-xl overflow-hidden border border-romcom-pink/20"
            >
              {romcomOfTheDay.backdropUrl && (
                <div className="relative h-52 overflow-hidden">
                  <img src={romcomOfTheDay.backdropUrl} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-[#faf8f6]" />
                </div>
              )}

              <div className="p-8">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="lg:w-1/5 flex-shrink-0">
                    <div className="aspect-[2/3] rounded-2xl overflow-hidden shadow-xl">
                      {romcomOfTheDay.posterUrl ? (
                        <img src={romcomOfTheDay.posterUrl} alt={romcomOfTheDay.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-romcom-purple to-romcom-blue flex items-center justify-center">
                          <Film className="w-16 h-16 text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-3xl font-playfair font-bold text-gray-800 mb-1">
                      {romcomOfTheDay.title}
                      {romcomOfTheDay.releaseYear && (
                        <span className="text-xl text-gray-400 font-normal ml-3">({romcomOfTheDay.releaseYear})</span>
                      )}
                    </h3>

                    {romcomRating && (
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-5 h-5 ${i < Math.floor(parseFloat(romcomRating)) ? 'fill-current' : ''}`} />
                          ))}
                        </div>
                        <span className="font-bold text-lg">{romcomRating}</span>
                        <span className="text-gray-400 text-sm">/ 5</span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-5">
                      {romcomOfTheDay.season && (
                        <span className="bg-romcom-blue/20 text-romcom-blue px-3 py-1 rounded-full text-sm capitalize">
                          {romcomOfTheDay.season === 'winter' ? '❄️' : romcomOfTheDay.season === 'spring' ? '🌸' : romcomOfTheDay.season === 'summer' ? '☀️' : '🍂'} {romcomOfTheDay.season}
                        </span>
                      )}
                      {romcomOfTheDay.moods?.slice(0, 3).map(mood => (
                        <span key={mood} className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm capitalize">
                          {mood.replace('_', ' ')}
                        </span>
                      ))}
                    </div>

                    {romcomOfTheDay.overview && (
                      <p className="text-gray-600 text-lg leading-relaxed mb-6 line-clamp-3">
                        {romcomOfTheDay.overview}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => navigate(`/movies/${romcomOfTheDay.id}`)}
                        className="btn-primary flex items-center gap-2 px-6 py-3"
                      >
                        View Details & Reviews
                      </button>
                      <button
                        onClick={() => toggleWatchlist(romcomOfTheDay.id)}
                        className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium border-2 transition-all ${
                          isRomcomWatchlisted ? 'bg-yellow-400 border-yellow-400 text-white' : 'border-gray-200 text-gray-600 hover:border-romcom-red hover:text-romcom-red'
                        }`}
                      >
                        <Bookmark className="w-5 h-5" />
                        {isRomcomWatchlisted ? 'Watchlisted' : 'Watchlist'}
                      </button>
                      {romcomOfTheDay.trailerUrl && (
                        <a
                          href={romcomOfTheDay.trailerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-5 py-3 rounded-full font-medium border-2 border-gray-200 text-gray-600 hover:border-romcom-red hover:text-romcom-red transition-all"
                        >
                          <Play className="w-5 h-5" />
                          Trailer
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="text-center py-8 text-gray-400">Could not load today's romcom 💕</div>
          )}
        </div>
      </section>

      {/* ── TRENDING ── */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-romcom-red" />
              <h2 className="text-3xl font-playfair font-bold text-gray-800">Trending Romcoms</h2>
            </div>
            <button onClick={() => navigate('/movies?sortBy=popularity')} className="text-romcom-red font-medium hover:underline text-sm">
              View All →
            </button>
          </div>

          {isLoadingTrending ? (
            <div className="flex justify-center py-16">
              <Loader className="w-10 h-10 text-romcom-red animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingMovies.map((movie, index) => (
                <motion.div
                  key={movie.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <MovieCard movie={movie} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── SEASONAL COLLECTIONS ── */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Calendar className="w-7 h-7 text-romcom-red" />
            <h2 className="text-3xl font-playfair font-bold text-gray-800">Seasonal Collections</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {seasonCollections.map((collection, index) => (
              <motion.div
                key={collection.season}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <SeasonCard
                  season={collection.season}
                  title={collection.title}
                  description={collection.description}
                  onClick={() => navigate(`/seasons/${collection.season}`)}
                />
              </motion.div>
            ))}
          </div>

          {/* Holiday Specials */}
          <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-6">Holiday Specials 🎉</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {HOLIDAY_COLLECTIONS.map((holiday, index) => (
              <motion.div
                key={holiday.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03, y: -4 }}
                onClick={() => navigate(`/movies?holiday=${holiday.holiday}`)}
                className={`${holiday.color} rounded-2xl p-8 text-white cursor-pointer shadow-lg`}
              >
                <div className="text-5xl mb-4">{holiday.icon}</div>
                <h4 className="text-2xl font-bold mb-2">{holiday.title}</h4>
                <p className="opacity-90 text-sm mb-5">{holiday.description}</p>
                <button className="bg-white/20 hover:bg-white/30 text-white px-5 py-2 rounded-full text-sm font-medium transition-all border border-white/30">
                  Browse Collection →
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COZY QUOTE ── */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-romcom-pink/10 via-transparent to-romcom-purple/5 -z-10" />

        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-3 mb-8">
            {[...Array(3)].map((_, i) => (
              <Heart key={i} className={`text-romcom-red fill-current ${i === 1 ? 'w-10 h-10' : 'w-6 h-6 mt-2 opacity-50'}`} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuote}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.5 }}
            >
              <blockquote className="text-3xl md:text-4xl font-playfair italic text-gray-800 mb-5 leading-relaxed">
                "{quotes[currentQuote].text}"
              </blockquote>
              <p className="text-romcom-red font-semibold text-lg">— {quotes[currentQuote].film}</p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex justify-center gap-3">
            {quotes.map((_, index) => (
              <button
                key={index}
                onClick={() => { setCurrentQuote(index); setActiveQuote(index); }}
                className={`rounded-full transition-all duration-300 ${
                  activeQuote === index ? 'w-8 h-3 bg-romcom-red' : 'w-3 h-3 bg-romcom-red/30 hover:bg-romcom-red/60'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Decorative swirl */}
        <div className="absolute -bottom-10 -left-10 w-48 h-48 opacity-10 pointer-events-none select-none">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path d="M100,10 C130,10 160,30 170,60 C180,90 165,125 140,140 C115,155 80,150 60,130 C40,110 35,80 50,58 C65,36 90,25 100,10Z" 
              fill="none" stroke="#9b6b8f" strokeWidth="3"/>
            <path d="M100,30 C120,30 145,45 152,68 C159,91 147,118 127,130 C107,142 80,138 63,122 C46,106 42,82 55,63 C68,44 88,35 100,30Z" 
              fill="none" stroke="#e8756a" strokeWidth="2"/>
            <path d="M55,160 C65,140 85,125 100,120 C115,115 135,118 148,130" 
              fill="none" stroke="#9b6b8f" strokeWidth="2" strokeLinecap="round"/>
            <path d="M30,100 C35,75 52,55 72,45" 
              fill="none" stroke="#c4a882" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </section>
    </div>
  );
};

export default HomePage;